/* PipelineAuditPage.jsx — Public Pipeline Exposure Audit calculator.
 *
 * A calm, deterministic, lead-generation audit tool that mirrors the
 * approved ChainFlow OS reference image — left-hero / centre-inputs
 * / right-preview layout, pill-shaped steppers, gradient CTA, soft
 * shadows, and the canonical violet/lavender palette.
 *
 * Discipline
 * ----------
 *   • Public route (no auth)
 *   • Pure deterministic arithmetic — POSTs to
 *     `/api/public/pipeline-audit` for replayable results
 *   • Calm operational framing · zero AI gimmickry · zero inflated
 *     savings claims
 *   • Uses existing `--cf-*` tokens · no new design language
 *   • Mobile-safe: stacks to single column under 1024 px
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus, Minus, ShieldCheck, CheckCircle2, Sparkles,
  ArrowUpRight, Activity, AlertTriangle, Wallet, Link2, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULTS = {
  monthly_transactions: 40,
  average_fee_gbp: 1_900,
  fall_through_pct: 30,
  avg_progression_days: 35,
  sales_staff: 15,
};

// Compact short-keys for shareable URLs:
//   /pipeline-audit?tx=40&fee=1900&ft=30&days=35&staff=15
const URL_KEYS = {
  monthly_transactions: "tx",
  average_fee_gbp:      "fee",
  fall_through_pct:     "ft",
  avg_progression_days: "days",
  sales_staff:          "staff",
};

const STEP = {
  monthly_transactions: { min: 0, max: 10_000, step: 1, fmt: (v) => v },
  average_fee_gbp:      { min: 0, max: 1_000_000, step: 100, fmt: (v) => `£${v.toLocaleString()}` },
  fall_through_pct:     { min: 0, max: 100, step: 1, fmt: (v) => `${v}%` },
  avg_progression_days: { min: 0, max: 365, step: 1, fmt: (v) => `${v} days` },
  sales_staff:          { min: 0, max: 500, step: 1, fmt: (v) => v },
};

const clampToStep = (k, n) => {
  const cfg = STEP[k];
  if (!cfg) return DEFAULTS[k];
  const x = Number.isFinite(n) ? Math.round(n) : DEFAULTS[k];
  return Math.max(cfg.min, Math.min(cfg.max, x));
};

const inputsFromSearchParams = (sp) => {
  const next = { ...DEFAULTS };
  Object.entries(URL_KEYS).forEach(([field, qk]) => {
    const raw = sp.get(qk);
    if (raw == null || raw === "") return;
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) next[field] = clampToStep(field, parsed);
  });
  return next;
};

const buildShareUrl = (inputs) => {
  const sp = new URLSearchParams();
  Object.entries(URL_KEYS).forEach(([field, qk]) => {
    sp.set(qk, String(inputs[field]));
  });
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/pipeline-audit?${sp.toString()}`;
};

/* Build the deterministic OG image URL (backend-rendered PNG) that
 * matches the current calculation. Same compact key contract used in
 * the share link.
 */
const buildOgUrl = (inputs) => {
  const sp = new URLSearchParams();
  Object.entries(URL_KEYS).forEach(([field, qk]) => {
    sp.set(qk, String(inputs[field]));
  });
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/api/public/pipeline-audit/og.png?${sp.toString()}`;
};

/* Set or update a head meta tag deterministically. */
const setMeta = (selector, attr, value) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // Parse the selector for property / name attributes — keeps the
    // helper generic without pulling in a meta library.
    const m = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const fmtGBP = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `£${Math.round(v / 1_000)}K`;
  return `£${Math.round(v).toLocaleString()}`;
};

const BAND_TONE = {
  Steady:   { fg: "#047857", bg: "rgba(5,150,105,0.10)",  dot: "#10B981" },
  Watching: { fg: "#92400E", bg: "rgba(245,158,11,0.12)", dot: "#F59E0B" },
  Slipping: { fg: "#9F1239", bg: "rgba(244,63,94,0.10)",  dot: "#F43F5E" },
};

/* ─── Stepper input ─────────────────────────────────────────────── */
const Stepper = ({ label, value, onChange, cfg, testid }) => {
  const dec = () => onChange(Math.max(cfg.min, value - cfg.step));
  const inc = () => onChange(Math.min(cfg.max, value + cfg.step));
  return (
    <div
      className="rounded-2xl px-5 py-4 flex items-center justify-between gap-3"
      style={{
        background: "var(--cf-card)",
        border: "1px solid var(--cf-border)",
        boxShadow: "var(--cf-elev-1)",
      }}
      data-testid={`pa-input-${testid}`}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-[10.5px] uppercase tracking-[0.16em] font-bold mb-1"
          style={{ color: "var(--cf-text-muted)" }}
        >
          {label}
        </p>
        <p className="text-[26px] font-bold tabular-nums leading-none"
           style={{ color: "var(--cf-text)" }}>
          {cfg.fmt(value)}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={dec}
          aria-label={`Decrease ${label}`}
          className="w-9 h-9 rounded-full inline-flex items-center justify-center transition-all hover:scale-105"
          style={{
            background: "rgba(124,58,237,0.08)",
            color: "var(--cf-indigo)",
            border: "1px solid rgba(124,58,237,0.16)",
          }}
          data-testid={`pa-step-down-${testid}`}
        >
          <Minus size={14} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={inc}
          aria-label={`Increase ${label}`}
          className="w-9 h-9 rounded-full inline-flex items-center justify-center transition-all hover:scale-105"
          style={{
            background: "rgba(124,58,237,0.08)",
            color: "var(--cf-indigo)",
            border: "1px solid rgba(124,58,237,0.16)",
          }}
          data-testid={`pa-step-up-${testid}`}
        >
          <Plus size={14} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
};

/* ─── Preview KPI card ─────────────────────────────────────────── */
const PreviewKPI = ({ icon: Icon, value, label, accent, testid }) => (
  <div
    className="rounded-2xl px-5 py-5"
    style={{
      background: "var(--cf-card)",
      border: "1px solid var(--cf-border)",
      boxShadow: "var(--cf-elev-2)",
    }}
    data-testid={`pa-kpi-${testid}`}
  >
    <div
      className="w-8 h-8 rounded-lg inline-flex items-center justify-center mb-3"
      style={{ background: `${accent}1A`, color: accent }}
    >
      <Icon size={14} strokeWidth={2.3} />
    </div>
    <p className="text-[28px] font-bold tabular-nums leading-none mb-1"
       style={{ color: "var(--cf-text)" }}>
      {value}
    </p>
    <p className="text-[11.5px] font-semibold leading-snug"
       style={{ color: "var(--cf-text-muted)" }}>
      {label}
    </p>
  </div>
);

/* ─── Band chip ─────────────────────────────────────────────────── */
const BandChip = ({ label, band, testid }) => {
  const tone = BAND_TONE[band] || BAND_TONE.Watching;
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-xl px-3 py-2"
      style={{
        background: "var(--cf-card-2)",
        border: "1px solid var(--cf-border-soft)",
      }}
      data-testid={`pa-band-${testid}`}
    >
      <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold"
         style={{ color: "var(--cf-text-muted)" }}>
        {label}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-[10.5px] font-bold px-2 py-0.5 rounded-full"
        style={{ background: tone.bg, color: tone.fg }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
        {band}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Page
   ─────────────────────────────────────────────────────────────────── */
export default function PipelineAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed state from URL — replayable from any shared link.
  const [inputs, setInputs] = useState(() => inputsFromSearchParams(searchParams));
  const [audit, setAudit] = useState(null);
  const [copied, setCopied] = useState(false);

  // Mirror inputs back into the URL (replace-state — no history flooding)
  // so refreshes and shares always reflect the current calculation.
  useEffect(() => {
    const sp = new URLSearchParams();
    Object.entries(URL_KEYS).forEach(([field, qk]) => {
      sp.set(qk, String(inputs[field]));
    });
    setSearchParams(sp, { replace: true });
  }, [inputs, setSearchParams]);

  // Sync Open Graph + Twitter meta tags so JS-aware crawlers (Slack,
  // Discord, LinkedIn, modern iMessage) unfurl the deterministic
  // per-share preview image. Older static crawlers fall back to the
  // canonical default in `public/index.html`.
  useEffect(() => {
    const ogUrl = buildOgUrl(inputs);
    const shareUrl = buildShareUrl(inputs);
    const title = "ChainFlow — Pipeline Exposure Audit";
    const description =
      "A deterministic operational audit · explainable arithmetic, replayable on any device.";
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", ogUrl);
    setMeta('meta[property="og:url"]', "content", shareUrl);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogUrl);
    setMeta('meta[name="description"]', "content", description);
    if (typeof document !== "undefined") {
      document.title = "Pipeline Exposure Audit · ChainFlow";
    }
  }, [inputs]);

  // Recompute on every input change (debounced via micro-throttle).
  useEffect(() => {
    let alive = true;
    const id = setTimeout(() => {
      api.post("/api/public/pipeline-audit", inputs)
        .then((res) => { if (alive) setAudit(res?.data?.audit || null); })
        .catch(() => { if (alive) setAudit(null); });
    }, 80);
    return () => { alive = false; clearTimeout(id); };
  }, [inputs]);

  const setField = (k) => (v) => setInputs((p) => ({ ...p, [k]: v }));

  const monthlyExposure = useMemo(
    () => audit?.monthly_fee_exposure_gbp ?? 0,
    [audit],
  );

  const onCopyShareUrl = async () => {
    const url = buildShareUrl(inputs);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Calm fallback for older browsers / non-secure contexts.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Calculation link copied", {
        description: "Paste it into email, Slack, or WhatsApp — the link recreates this exact audit.",
      });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy the link", {
        description: "Long-press the address bar to copy manually.",
      });
    }
  };

  return (
    <div
      className="min-h-screen px-5 sm:px-9 lg:px-14 py-10 lg:py-14"
      style={{
        background:
          "linear-gradient(180deg, var(--cf-app-bg) 0%, var(--cf-app-bg-2) 100%)",
      }}
      data-testid="pipeline-audit-page"
    >
      {/* Sticky calm header */}
      <div className="max-w-7xl mx-auto mb-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="pa-brand">
          <div
            className="w-9 h-9 rounded-xl inline-flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, var(--cf-indigo) 0%, var(--cf-indigo-deep) 100%)",
              boxShadow: "var(--cf-elev-indigo)",
            }}
          >
            <Activity size={16} className="text-white" strokeWidth={2.4} />
          </div>
          <span className="text-[15px] font-bold tracking-tight"
                style={{ color: "var(--cf-text)" }}>
            ChainFlow
          </span>
        </Link>
        <Link
          to="/signup"
          className="text-[13px] font-semibold transition-colors"
          style={{ color: "var(--cf-indigo)" }}
          data-testid="pa-signup-link"
        >
          Sign in →
        </Link>
      </div>

      <div
        className="max-w-7xl mx-auto rounded-[28px] p-6 sm:p-10 lg:p-12"
        style={{
          background: "var(--cf-card)",
          border: "1px solid var(--cf-border)",
          boxShadow: "var(--cf-elev-4)",
        }}
        data-testid="pa-shell"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {/* ─── Left · Hero / context ────────────────────────────── */}
          <section className="lg:col-span-5" data-testid="pa-hero">
            <div className="inline-flex items-center gap-2 mb-5">
              <Sparkles size={12} style={{ color: "var(--cf-indigo)" }}
                        strokeWidth={2.4} />
              <p
                className="text-[10.5px] uppercase tracking-[0.22em] font-bold"
                style={{ color: "var(--cf-indigo)" }}
              >
                Pipeline Exposure Audit
              </p>
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-[44px] font-bold leading-[1.05] mb-5"
              style={{ color: "var(--cf-text)" }}
              data-testid="pa-h1"
            >
              How much is poor progression costing your business?
            </h1>
            <p
              className="text-[14.5px] leading-relaxed mb-7 max-w-md"
              style={{ color: "var(--cf-text-muted)" }}
            >
              A calm, deterministic audit of your monthly pipeline.
              Move the sliders below — every number is plain arithmetic
              you can verify on a calculator. No AI projections, no
              inflated savings.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Built for UK estate agencies",
                "Benchmarked against high-performing teams",
                "No card required · no email gate",
                "Replayable maths · explainable on demand",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2.5">
                  <CheckCircle2 size={14} strokeWidth={2.4}
                                style={{ color: "var(--cf-indigo)" }} />
                  <span className="text-[13.5px]"
                        style={{ color: "var(--cf-text-2)" }}>
                    {t}
                  </span>
                </li>
              ))}
            </ul>

            <div
              className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
              style={{
                background: "var(--cf-lavender)",
                border: "1px solid var(--cf-border-soft)",
              }}
              data-testid="pa-trust-strip"
            >
              <ShieldCheck size={14} strokeWidth={2.4}
                           style={{ color: "var(--cf-indigo-deep)" }} />
              <p className="text-[11.5px] font-semibold"
                 style={{ color: "var(--cf-text-2)" }}>
                Inputs stay on this page · nothing is stored or shared.
              </p>
            </div>
          </section>

          {/* ─── Middle · Your inputs ─────────────────────────────── */}
          <section className="lg:col-span-4 space-y-3" data-testid="pa-inputs">
            <p
              className="text-[10.5px] uppercase tracking-[0.22em] font-bold mb-3"
              style={{ color: "var(--cf-text-muted)" }}
            >
              Your inputs
            </p>
            <Stepper
              label="Monthly transactions"
              value={inputs.monthly_transactions}
              onChange={setField("monthly_transactions")}
              cfg={STEP.monthly_transactions}
              testid="transactions"
            />
            <Stepper
              label="Average fee"
              value={inputs.average_fee_gbp}
              onChange={setField("average_fee_gbp")}
              cfg={STEP.average_fee_gbp}
              testid="fee"
            />
            <Stepper
              label="Fall-through rate"
              value={inputs.fall_through_pct}
              onChange={setField("fall_through_pct")}
              cfg={STEP.fall_through_pct}
              testid="fall-through"
            />
            <Stepper
              label="Avg progression days"
              value={inputs.avg_progression_days}
              onChange={setField("avg_progression_days")}
              cfg={STEP.avg_progression_days}
              testid="days"
            />
            <Stepper
              label="Sales staff"
              value={inputs.sales_staff}
              onChange={setField("sales_staff")}
              cfg={STEP.sales_staff}
              testid="staff"
            />
          </section>

          {/* ─── Right · Live preview ────────────────────────────── */}
          <section className="lg:col-span-3 flex flex-col gap-3" data-testid="pa-preview">
            <p
              className="text-[10.5px] uppercase tracking-[0.22em] font-bold mb-1"
              style={{ color: "var(--cf-text-muted)" }}
            >
              Live preview
            </p>

            <PreviewKPI
              icon={AlertTriangle}
              value={audit?.deals_at_risk_per_month ?? 0}
              label="Deals at risk / month"
              accent="#F59E0B"
              testid="risk"
            />
            <PreviewKPI
              icon={Wallet}
              value={fmtGBP(monthlyExposure)}
              label="Monthly fee exposure"
              accent="#F43F5E"
              testid="exposure"
            />
            <PreviewKPI
              icon={Activity}
              value={fmtGBP(audit?.annual_revenue_leakage_gbp ?? 0)}
              label="Annual revenue leakage"
              accent="var(--cf-indigo)"
              testid="leakage"
            />

            {/* Calm band strip */}
            <div className="grid grid-cols-1 gap-2 mt-1">
              <BandChip
                label="Transaction stability"
                band={audit?.transaction_stability_band || "Watching"}
                testid="stability"
              />
              <BandChip
                label="Pipeline pressure"
                band={audit?.pipeline_pressure_band || "Watching"}
                testid="pressure"
              />
              <BandChip
                label="Progression runway"
                band={audit?.progression_runway_band || "Watching"}
                testid="runway"
              />
            </div>

            {/* Recovery CTA card — calm, capped at 30 % */}
            <div
              className="rounded-2xl px-5 py-5 mt-3"
              style={{
                background:
                  "linear-gradient(135deg, var(--cf-lavender) 0%, var(--cf-lavender-2) 100%)",
                border: "1px solid rgba(124,58,237,0.18)",
                boxShadow: "var(--cf-elev-1)",
              }}
              data-testid="pa-recovery-card"
            >
              <p
                className="text-[10.5px] uppercase tracking-[0.16em] font-bold mb-2"
                style={{ color: "var(--cf-indigo-deep)" }}
              >
                Recovery potential
              </p>
              <p
                className="text-[24px] font-bold tabular-nums leading-tight mb-1"
                style={{ color: "var(--cf-text)" }}
              >
                {fmtGBP(audit?.projected_revenue_recovered_gbp ?? 0)} / yr
              </p>
              <p
                className="text-[11.5px] leading-snug"
                style={{ color: "var(--cf-text-muted)" }}
              >
                Conservative cap at 30 % of monthly fee exposure ·
                what disciplined progression typically returns. No
                guarantees, no inflated multipliers.
              </p>
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <Link
                to="/signup"
                className="rounded-2xl px-5 py-4 text-white text-center text-[15px] font-bold inline-flex items-center justify-center gap-2 transition-transform hover:translate-y-[-1px]"
                style={{
                  background:
                    "linear-gradient(135deg, var(--cf-indigo) 0%, var(--cf-indigo-deep) 100%)",
                  boxShadow: "var(--cf-elev-indigo)",
                }}
                data-testid="pa-cta"
              >
                Run the full operational audit
                <ArrowUpRight size={15} strokeWidth={2.4} />
              </Link>
              <button
                type="button"
                onClick={onCopyShareUrl}
                className="rounded-2xl px-5 py-3 text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                style={{
                  background: "var(--cf-card-2)",
                  border: "1px solid var(--cf-border)",
                  color: "var(--cf-text-2)",
                }}
                aria-live="polite"
                data-testid="pa-share-button"
              >
                {copied ? (
                  <>
                    <Check size={13} strokeWidth={2.4}
                           style={{ color: "var(--cf-success)" }} />
                    Calculation link copied
                  </>
                ) : (
                  <>
                    <Link2 size={13} strokeWidth={2.4}
                           style={{ color: "var(--cf-indigo)" }} />
                    Share calculation
                  </>
                )}
              </button>
              <p
                className="text-[10.5px] text-center"
                style={{ color: "var(--cf-text-soft)" }}
                data-testid="pa-share-helper"
              >
                Link encodes inputs only · no accounts, no tracking, no backend.
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Trust footnote */}
      <p
        className="max-w-7xl mx-auto mt-6 text-[11px] text-center"
        style={{ color: "var(--cf-text-soft)" }}
        data-testid="pa-trust-footnote"
      >
        Every figure is plain arithmetic — replayable, explainable, no
        speculative AI. Source: <code style={{ color: "var(--cf-text-muted)" }}>
        pipeline_audit_engine.v1</code>.
      </p>
    </div>
  );
}
